import type { CSSProperties } from 'react';

/**
 * Price display for Pack tiles and Pack detail pages.
 *
 * Per frontend.md §15.2:
 *  - `price_status == "OK"`: show `price_cents / 100` as USD.
 *  - `price_status != "OK"` or missing: show "Price unavailable".
 *  - Never render `null`/`undefined` directly.
 *
 * NOTE: Pack items do not carry a `price_status` field — Packs are
 * operator-priced and assumed OK. This component still accepts a status
 * prop so the same component works for Card-level prices (Phase 3+)
 * where `price_status` comes from the prices team pipeline.
 */

export type PriceStatus = 'OK' | 'NOT_FOUND' | 'ERROR' | 'PENDING';

export interface PriceDisplayProps {
  price_cents: number | null | undefined;
  /**
   * Optional price status. If omitted, the component assumes OK *only* if
   * `price_cents` is a positive integer. Any other combination renders
   * "Price unavailable".
   */
  status?: PriceStatus;
  style?: CSSProperties;
}

const UNAVAILABLE = 'Price unavailable';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function formatPrice(
  priceCents: number | null | undefined,
  status?: PriceStatus,
): string {
  if (status !== undefined && status !== 'OK') return UNAVAILABLE;
  if (typeof priceCents !== 'number' || !Number.isFinite(priceCents)) {
    return UNAVAILABLE;
  }
  if (priceCents < 0) return UNAVAILABLE;
  return usd.format(priceCents / 100);
}

export function PriceDisplay(props: PriceDisplayProps): JSX.Element {
  const label = formatPrice(props.price_cents, props.status);
  const style: CSSProperties = props.style ?? {};
  return (
    <span
      className="tabular-nums font-semibold text-text-0"
      data-testid="price-display"
      style={style}
    >
      {label}
    </span>
  );
}
